import type { Project, ProjectListItem } from '@p2b/contract';
import { useQuery } from '@tanstack/react-query';
import { Suspense, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { ErrorNote, useMe } from '../App.js';
import { api } from '../api.js';
import { ProjectsMap } from '../lazyMap.js';
import { fill, formatDay, label, useI18n } from '../i18n.js';
import { Icon } from '../ui.js';
import {
  attentionItems,
  DEFAULT_SORT,
  filterHome,
  nextSort,
  orgsOf,
  sortProjects,
  spansOrgs,
  straightTo,
  type AttentionItem,
  type HomeFilter,
  type Sort,
  type SortKey,
} from './home/homeParts.js';
import { AcceptButton, InvitationFacts } from './Invite.js';
import { Segmented } from './org/widgets.js';
import { EmptyState, projectState, relativeDay, type ProjectState } from './orgParts.js';
import { NewProjectDialog } from './project/ProjectDialogs.js';

export function Home() {
  const me = useMe();
  const invitations = me.data?.invitations ?? [];
  return (
    <div className="page home">
      {invitations.length > 0 && <Invitations />}
      {me.data?.pending ? invitations.length === 0 && <Waiting /> : <Projects />}
    </div>
  );
}

/** Invitations to this address, accepted right here after signing in. */
function Invitations() {
  const { t } = useI18n();
  const me = useMe();
  return (
    <section className="invitations">
      <h1 className="page-title">{t('youHaveInvitations')}</h1>
      {(me.data?.invitations ?? []).map((inv) => (
        <div key={inv.id} className="panel stack">
          <p>
            <strong>{inv.inviterName ?? 'Mynstra'}</strong> {t('inviteIntro')}
          </p>
          <InvitationFacts inv={inv} />
          <div>
            <AcceptButton inv={inv} />
          </div>
        </div>
      ))}
    </section>
  );
}

/** Signed in, but nothing given yet (DESIGN §2). */
function Waiting() {
  const { t } = useI18n();
  const me = useMe();
  return (
    <section className="waiting">
      <h1 className="page-title">{t('waitingTitle')}</h1>
      <p className="lead">{t('waitingText')}</p>
      <p className="muted">{me.data?.user.email}</p>
    </section>
  );
}

/** The line under a project's name: its description, else where it is (without repeating the name). */
export function projectPlace(p: Project): string {
  if (p.description) return p.description;
  return [p.address && p.address !== p.name ? p.address : null, p.municipality].filter(Boolean).join(', ');
}

/** Toolbar from this many projects: below it there is nothing to search. */
const TOOLBAR_FROM = 6;

/**
 * The signed-in home (owner's layout, 2026-11-01): one register of every project the caller can see (Listi) or the
 * same projects on a map (Kort), "Bíður þín" above them when something waits, "Nýtt verkefni" at the right.
 */
function Projects() {
  const { t } = useI18n();
  const me = useMe();
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'map' ? 'map' : 'list';
  const projects = useQuery({ queryKey: ['projects'], queryFn: () => api.projects() });
  const [filter, setFilter] = useState<HomeFilter>({ q: '', org: '', state: 'all' });
  const [sort, setSort] = useState<Sort>(DEFAULT_SORT);
  const [creating, setCreating] = useState(false);
  const superadmin = Boolean(me.data?.user.isSuperadmin);
  const orgs = useQuery({ queryKey: ['orgs'], queryFn: () => api.orgs(), enabled: superadmin });

  const go = straightTo(me.data, projects.data);
  if (go) return <Navigate to={go} replace />;

  const all = projects.data ?? [];
  const list = sortProjects(filterHome(all, filter), sort);
  const orgChoices = orgsOf(all);
  const count = (s: ProjectState) => all.filter((p) => projectState(p) === s).length;
  const attention = attentionItems(all, superadmin ? (me.data?.pendingUsers ?? 0) : 0);
  const setView = (v: 'list' | 'map') =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (v === 'map') next.set('view', 'map');
        else next.delete('view');
        return next;
      },
      { replace: true },
    );

  return (
    <>
      <header className="home-head">
        <h1 className="page-title">
          {t('projects')}
          {all.length > 0 && <span className="title-count">{all.length}</span>}
        </h1>
        {superadmin && (
          <button className="primary" onClick={() => setCreating(true)}>
            <Icon.Plus />
            {t('newProject')}
          </button>
        )}
      </header>
      {attention.length > 0 && <Attention items={attention} />}
      <ErrorNote error={projects.error} />
      {projects.isPending && <p className="muted">{t('loading')}</p>}
      {projects.data?.length === 0 && <EmptyState text={t('noProjects')} />}
      {all.length > 0 && (
        <>
          <div className="toolbar home-toolbar">
            {all.length >= TOOLBAR_FROM && (
              <label className="search grow-sm">
                <Icon.Search className="search-icon" />
                <input
                  type="search"
                  aria-label={t('searchProjects')}
                  placeholder={t('searchProjects')}
                  value={filter.q}
                  onChange={(e) => setFilter({ ...filter, q: e.target.value })}
                />
              </label>
            )}
            {orgChoices.length > 1 && (
              <select
                className="org-filter"
                aria-label={t('organization')}
                value={filter.org}
                onChange={(e) => setFilter({ ...filter, org: e.target.value })}
              >
                <option value="">{t('allOrgs')}</option>
                {orgChoices.map((o) => (
                  <option key={o.slug} value={o.slug}>
                    {o.name}
                  </option>
                ))}
              </select>
            )}
            {all.length >= TOOLBAR_FROM && (
              <Segmented
                className="seg-state"
                label={t('status')}
                value={filter.state}
                onChange={(state) => setFilter({ ...filter, state })}
                options={[
                  { value: 'all', label: t('all') },
                  { value: 'inProgress', label: t('inProgress'), count: count('inProgress') },
                  { value: 'delivered', label: t('delivered'), count: count('delivered') },
                ]}
              />
            )}
            <span className="toolbar-gap" />
            <Segmented
              className="seg-view"
              label={t('view')}
              value={view}
              onChange={setView}
              options={[
                { value: 'list', label: t('viewList') },
                { value: 'map', label: t('viewMap') },
              ]}
            />
          </div>
          {view === 'map' ? (
            <Suspense fallback={<div className="mapview loading" />}>
              <ProjectsMap
                projects={list}
                renderCard={(p) => <MapCard p={p} />}
                renderRow={(p) => <MapRow p={p} multi={spansOrgs(all)} />}
              />
            </Suspense>
          ) : list.length === 0 ? (
            <p className="empty">{t('noMatch')}</p>
          ) : (
            <Register
              list={list}
              sort={sort}
              onSort={(k) => setSort(nextSort(sort, k))}
              multiOrg={spansOrgs(all)}
            />
          )}
        </>
      )}
      {creating && (
        <NewProjectDialog
          orgs={(orgs.data ?? []).map((o) => ({ slug: o.slug, name: o.name }))}
          onClose={() => setCreating(false)}
        />
      )}
    </>
  );
}

const href = (p: ProjectListItem) => `/projects/${p.org}/${p.slug}`;

/** "Bíður þín": a strip of links, shown only when something waits. */
function Attention({ items }: { items: AttentionItem[] }) {
  const { t, lang } = useI18n();
  return (
    <section className="attention" aria-label={t('waitingOnYou')}>
      <span className="attention-label">{t('waitingOnYou')}</span>
      {items.map((it) => {
        if (it.kind === 'users')
          return (
            <Link key="users" to="/admin/users" className="attention-item">
              <span className="attention-icon">
                <Icon.User />
              </span>
              <span>{it.n === 1 ? t('userWaiting') : fill(t('usersWaiting'), { n: it.n })}</span>
            </Link>
          );
        if (it.kind === 'undelivered')
          return (
            <Link key={`u-${it.project.id}`} to={`${href(it.project)}/deliveries`} className="attention-item">
              <span className="attention-icon">
                <Icon.Frozen />
              </span>
              <span>
                <strong>{it.project.name}</strong> · {fill(t('frozenOn'), { date: formatDay(it.at, lang) })}
              </span>
            </Link>
          );
        return (
          <Link
            key={`i-${it.project.id}`}
            to={`${href(it.project)}/files?folder=inbox`}
            className="attention-item"
          >
            <span className="attention-icon">
              <Icon.Inbox />
            </span>
            <span>
              <strong>{it.project.name}</strong> ·{' '}
              {it.n === 1 ? t('inboxNewOne') : fill(t('inboxNew'), { n: it.n })}
            </span>
          </Link>
        );
      })}
    </section>
  );
}

/** The project's model still (see ProjectListItem.stillUrl); a building glyph when it has none. */
function Thumb({ src }: { src?: string | null | undefined }) {
  const [failed, setFailed] = useState(false);
  return (
    <span className={`thumb${src && !failed ? ' still' : ''}`} aria-hidden="true">
      {src && !failed ? (
        <img src={src} alt="" loading="lazy" decoding="async" onError={() => setFailed(true)} />
      ) : (
        <Icon.Building />
      )}
    </span>
  );
}

function StateTag({ p }: { p: ProjectListItem }) {
  const { t } = useI18n();
  const st = projectState(p);
  return <span className={`tag dot${st === 'delivered' ? ' ok' : ''}`}>{t(st)}</span>;
}

function Latest({ p }: { p: ProjectListItem }) {
  const { t, lang } = useI18n();
  if (!p.latestPublication)
    return (
      <span className="muted">
        {t('noPublication')}
        {p.attention?.undelivered && <span className="flag"> · {t('frozen').toLowerCase()}</span>}
      </span>
    );
  return (
    <>
      <span className="pub-line">
        <Icon.Model width={15} height={15} />
        {p.latestPublication.title || p.latestPublication.revisionLabel}
      </span>
      <div className="sub">{formatDay(p.latestPublication.publishedAt, lang)}</div>
    </>
  );
}

/** Dates open newest first (descending); text columns A→Ö (ascending). */
function ariaSort(sort: Sort): 'ascending' | 'descending' {
  const natural = sort.key === 'latest' || sort.key === 'activity' ? 'descending' : 'ascending';
  if (sort.dir === 1) return natural;
  return natural === 'ascending' ? 'descending' : 'ascending';
}

/** The register: one 56 px row per project, sortable by every column; cards on a phone. */
function Register({
  list,
  sort,
  onSort,
  multiOrg,
}: {
  list: ProjectListItem[];
  sort: Sort;
  onSort: (k: SortKey) => void;
  multiOrg: boolean;
}) {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const showRole = list.some((p) => p.role && p.role !== 'client');
  const cols: { key: SortKey; label: string; className?: string }[] = [
    { key: 'name', label: t('projects') },
    ...(multiOrg ? [{ key: 'org' as const, label: t('organization'), className: 'hide-sm' }] : []),
    { key: 'latest', label: t('latestPublication'), className: 'hide-sm' },
    { key: 'status', label: t('status') },
    { key: 'activity', label: t('lastActivity'), className: 'hide-sm' },
    ...(showRole ? [{ key: 'role' as const, label: t('yourRole'), className: 'hide-sm' }] : []),
  ];
  return (
    <div className="sheet">
      <table className="stack-sm register-table">
        <thead>
          <tr>
            {cols.map((c) => (
              <th
                key={c.key}
                className={c.className}
                aria-sort={sort.key === c.key ? ariaSort(sort) : undefined}
              >
                <button
                  type="button"
                  className="th-sort"
                  onClick={() => onSort(c.key)}
                  title={fill(t('sortBy'), { col: c.label })}
                >
                  {c.label}
                  {sort.key === c.key && <Icon.Sort dir={sort.dir} />}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {list.map((p) => (
            <tr key={p.id} className="link-row" onClick={() => navigate(href(p))}>
              <td className="cell-grow">
                <span className="proj">
                  <Thumb src={p.stillUrl} />
                  <span className="proj-text">
                    <Link to={href(p)} className="cell-strong row-link" onClick={(e) => e.stopPropagation()}>
                      {p.name}
                    </Link>
                    <span className="sub">{projectPlace(p) || '\u00a0'}</span>
                    {/* On a phone the row is a card: the organization and the newest delivery under the name. */}
                    <span className="sub show-sm">
                      {multiOrg ? `${p.orgName} · ` : ''}
                      {p.latestPublication
                        ? `${p.latestPublication.title || p.latestPublication.revisionLabel} · ${formatDay(p.latestPublication.publishedAt, lang)}`
                        : t('noPublication')}
                    </span>
                  </span>
                </span>
              </td>
              {multiOrg && <td className="hide-sm">{p.orgName}</td>}
              <td className="hide-sm">
                <Latest p={p} />
              </td>
              <td>
                <StateTag p={p} />
              </td>
              <td className="hide-sm nowrap">
                {p.lastActivityAt ? relativeDay(p.lastActivityAt, lang, t) : <span className="muted">—</span>}
              </td>
              {showRole && (
                <td className="hide-sm">{label(t, 'role', p.role) || <span className="muted">—</span>}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MapRow({ p, multi }: { p: ProjectListItem; multi: boolean }) {
  const { t } = useI18n();
  return (
    <span className="maplist-text">
      <span className="maplist-name">{p.name}</span>
      <span className="sub">
        {[multi ? p.orgName : projectPlace(p), t(projectState(p))].filter(Boolean).join(' · ')}
      </span>
    </span>
  );
}

function MapCard({ p }: { p: ProjectListItem }) {
  const { t, lang } = useI18n();
  return (
    <div className="map-card-body">
      <div className="cell-strong">{p.name}</div>
      <div className="sub">{[projectPlace(p), p.orgName].filter(Boolean).join(' · ')}</div>
      <div className="map-card-row">
        <StateTag p={p} />
        <span className="sub">
          {p.latestPublication
            ? `${p.latestPublication.title || p.latestPublication.revisionLabel}, ${formatDay(p.latestPublication.publishedAt, lang)}`
            : t('noPublication')}
        </span>
      </div>
      <div className="map-card-row">
        <span className="sub">
          {p.lastActivityAt ? fill(t('changedWhen'), { when: relativeDay(p.lastActivityAt, lang, t) }) : ''}
        </span>
        <Link className="button primary sm" to={href(p)}>
          {t('open')}
        </Link>
      </div>
    </div>
  );
}
